import React from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    SelectInput,
    SelectArrayInput,
    BooleanInput,
    required,
    minLength,
    maxLength,
    useTranslate,
    useRedirect,
    useNotify,
    SaveButton,
    Toolbar,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    IconButton,
    Alert,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material';
import {
    ArrowBack,
    Groups as GroupsIcon,
    Settings,
} from '@mui/icons-material';
import { ServerErrorBinder } from '../../common/ServerErrorBinder';
import { translateServerMessage } from '../../../utils/translateServerMessage';

const NOTIFICATION_GROUP_KNOWN_FIELDS = new Set([
    'name', 'description', 'type', 'channels', 'active',
]);

const mapNotificationGroupBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const notificationGroupFieldLabelKey = (field: string): string =>
    NOTIFICATION_GROUP_KNOWN_FIELDS.has(field)
        ? `resources.notification-groups.fields.${field}`
        : field;

const CreateToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: 4,
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 4,
        }}>
            <IconButton
                onClick={() => redirect('/notification-groups')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.notification-groups.create.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.notification-groups.create.title')}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {translate('resources.notification-groups.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

export const GroupCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const theme = useTheme();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    const typeChoices = [
        { id: 'site', name: translate('resources.notification-groups.types.site') },
        { id: 'charger', name: translate('resources.notification-groups.types.charger') },
        { id: 'payment', name: translate('resources.notification-groups.types.payment') },
        { id: 'system', name: translate('resources.notification-groups.types.system') },
        { id: 'custom', name: translate('resources.notification-groups.types.custom') },
    ];

    const channelChoices = [
        { id: 'email', name: translate('resources.notification-groups.channels.email') },
        { id: 'sms', name: translate('resources.notification-groups.channels.sms') },
        { id: 'whatsapp', name: translate('resources.notification-groups.channels.whatsapp') },
        { id: 'push', name: translate('resources.notification-groups.channels.push') },
        { id: 'slack', name: translate('resources.notification-groups.channels.slack') },
    ];

    return (
        <Create
            actions={false}
            title={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: () => {
                    setServerErrors({});
                    notify('resources.notification-groups.notifications.createSuccess', { type: 'success' });
                    redirect('/notification-groups');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapNotificationGroupBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(notificationGroupFieldLabelKey(field), { _: field });
                            summaryLines.push(`\u2022 ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                },
            }}
            sx={{
                backgroundColor: 'transparent',
                maxWidth: '100%',
                overflow: 'hidden',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    {/* Basic Information */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <GroupsIcon sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.notification-groups.sections.basicInfo')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="name"
                                    label="resources.notification-groups.fields.name"
                                    validate={[required(), minLength(2), maxLength(100)]}
                                    fullWidth
                                    helperText="resources.notification-groups.help.name"
                                />

                                <SelectInput
                                    source="type"
                                    label="resources.notification-groups.fields.type"
                                    choices={typeChoices}
                                    validate={[required()]}
                                    fullWidth
                                    helperText="resources.notification-groups.help.type"
                                />

                                <TextInput
                                    source="description"
                                    label="resources.notification-groups.fields.description"
                                    multiline
                                    rows={3}
                                    fullWidth
                                    helperText="resources.notification-groups.help.description"
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Channels Configuration */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.notification-groups.sections.channelsConfig')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <SelectArrayInput
                                    source="channels"
                                    label="resources.notification-groups.fields.channels"
                                    choices={channelChoices}
                                    defaultValue={['email']}
                                    validate={[required()]}
                                    fullWidth
                                    helperText="resources.notification-groups.help.channels"
                                />

                                <BooleanInput
                                    source="active"
                                    label="resources.notification-groups.fields.active"
                                    defaultValue={true}
                                    helperText="resources.notification-groups.help.active"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};
